import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import { Helmet } from 'react-helmet-async';
import { settingsApi, type AppSettingDto } from '../../api/settingsApi';
import { PageHeader } from '../../components/PageHeader';

/** Labely polí v UI — nahradit technické klíče lidsky čitelnými. */
const FIELD_LABELS: Record<string, string> = {
  EMAIL_FROM: 'Adresa odesílatele',
  EMAIL_FROM_NAME: 'Jméno odesílatele',
  SMTP_HOST: 'SMTP server',
  SMTP_PORT: 'SMTP port',
  SMTP_USERNAME: 'SMTP uživatelské jméno',
  SMTP_PASSWORD: 'SMTP heslo',
  SMTP_STARTTLS: 'Použít STARTTLS',
  EMAIL_ENABLED: 'Povolit odesílání',
  APP_PUBLIC_URL: 'Veřejná URL aplikace',
};

const GROUP_LABELS: Record<string, string> = {
  EMAIL: 'E-mail (SMTP)',
  GENERAL: 'Obecné',
};

/** Dvousloupcový layout pro úzké SMTP fieldy (host/port/username atd.). */
const FIELD_WIDTHS: Record<string, number> = {
  SMTP_HOST: 8,
  SMTP_PORT: 4,
  SMTP_USERNAME: 8,
  SMTP_STARTTLS: 4,
  SMTP_PASSWORD: 12,
  EMAIL_FROM: 6,
  EMAIL_FROM_NAME: 6,
};

export const SettingsPage = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [testDialog, setTestDialog] = useState(false);

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['superadmin', 'settings'],
    queryFn: settingsApi.list,
  });

  const grouped = useMemo(() => {
    const map: Record<string, AppSettingDto[]> = {};
    for (const s of settings) {
      if (!map[s.group]) map[s.group] = [];
      map[s.group].push(s);
    }
    return map;
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: settingsApi.update,
    onSuccess: () => {
      enqueueSnackbar('Nastavení uloženo', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'settings'] });
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Nastavení | SUPER ADMIN</title>
      </Helmet>
      <PageHeader
        title="Nastavení aplikace"
        subtitle="Globální konfigurace ukládaná v databázi (.properties slouží jako fallback)"
      />

      <Stack spacing={3} sx={{ maxWidth: 880 }}>
        {Object.entries(grouped).map(([group, items]) => (
          <SettingsGroupCard
            key={group}
            group={group}
            items={items}
            onSave={(values) => updateMutation.mutate(values)}
            saving={updateMutation.isPending}
            onOpenEmailTest={() => setTestDialog(true)}
          />
        ))}
      </Stack>

      <TestEmailDialog open={testDialog} onClose={() => setTestDialog(false)} />
    </>
  );
};

interface SettingsGroupCardProps {
  group: string;
  items: AppSettingDto[];
  onSave: (values: Record<string, string>) => void;
  saving: boolean;
  onOpenEmailTest: () => void;
}

const SettingsGroupCard = ({ group, items, onSave, saving, onOpenEmailTest }: SettingsGroupCardProps) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const initial: Record<string, string> = {};
    for (const i of items) initial[i.key] = i.sensitive ? '' : i.value ?? '';
    setValues(initial);
    setDirty({});
  }, [items]);

  const handleChange = (key: string, value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const handleSave = () => {
    const payload: Record<string, string> = {};
    for (const k of Object.keys(dirty)) {
      payload[k] = values[k];
    }
    if (Object.keys(payload).length === 0) return;
    onSave(payload);
    setDirty({});
  };

  const hasDirty = Object.keys(dirty).length > 0;
  const isEmail = group === 'EMAIL';
  const isEnabled = values.EMAIL_ENABLED === 'true' || items.find((i) => i.key === 'EMAIL_ENABLED')?.value === 'true';
  const hasPassword = items.find((i) => i.key === 'SMTP_PASSWORD')?.hasValue;

  // Sorted items - prehled-friendly order pro EMAIL skupinu
  const ordered = isEmail
    ? [...items].sort((a, b) => {
        const order = ['EMAIL_ENABLED', 'SMTP_HOST', 'SMTP_PORT', 'SMTP_STARTTLS', 'SMTP_USERNAME', 'SMTP_PASSWORD', 'EMAIL_FROM', 'EMAIL_FROM_NAME'];
        return (order.indexOf(a.key) === -1 ? 99 : order.indexOf(a.key)) - (order.indexOf(b.key) === -1 ? 99 : order.indexOf(b.key));
      })
    : items;

  const renderField = (s: AppSettingDto) => {
    if (s.key === 'EMAIL_ENABLED') {
      return (
        <Grid size={12} key={s.key}>
          <FormControlLabel
            control={
              <Switch
                checked={values[s.key] === 'true'}
                onChange={(e) => handleChange(s.key, e.target.checked ? 'true' : 'false')}
              />
            }
            label={
              <Stack>
                <Typography fontWeight={600}>{FIELD_LABELS[s.key] ?? s.key}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.description}
                </Typography>
              </Stack>
            }
          />
          <Divider sx={{ mt: 1 }} />
        </Grid>
      );
    }
    if (s.key === 'SMTP_STARTTLS') {
      return (
        <Grid size={{ xs: 12, sm: FIELD_WIDTHS[s.key] ?? 12 }} key={s.key}>
          <FormControlLabel
            control={
              <Switch
                checked={values[s.key] === 'true'}
                onChange={(e) => handleChange(s.key, e.target.checked ? 'true' : 'false')}
              />
            }
            label={<Typography variant="body2">{FIELD_LABELS[s.key] ?? s.key}</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
            {s.description}
          </Typography>
        </Grid>
      );
    }
    const isPort = s.key === 'SMTP_PORT';
    return (
      <Grid size={{ xs: 12, sm: FIELD_WIDTHS[s.key] ?? 12 }} key={s.key}>
        <TextField
          label={FIELD_LABELS[s.key] ?? s.key}
          helperText={s.description}
          value={values[s.key] ?? ''}
          onChange={(e) => handleChange(s.key, e.target.value)}
          type={s.sensitive ? 'password' : isPort ? 'number' : 'text'}
          fullWidth
          placeholder={s.sensitive && s.hasValue ? '(uloženo — zadejte pro změnu)' : ''}
          size="small"
        />
      </Grid>
    );
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isEmail && <EmailIcon color="primary" />}
            <Typography variant="h5">{GROUP_LABELS[group] ?? group}</Typography>
          </Stack>
          {isEmail && (
            <Button
              startIcon={<SendIcon />}
              variant="outlined"
              onClick={onOpenEmailTest}
              disabled={!isEnabled || !hasPassword}
            >
              Otestovat odeslání
            </Button>
          )}
        </Stack>

        {isEmail && !hasPassword && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Pole <strong>SMTP heslo</strong> je prázdné. Bez vyplněného hesla nelze odesílat e-maily.
          </Alert>
        )}

        <Grid container spacing={2}>
          {ordered.map(renderField)}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasDirty || saving}
          >
            {saving ? 'Ukládám…' : hasDirty ? `Uložit změny (${Object.keys(dirty).length})` : 'Uložit'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

interface TestEmailDialogProps {
  open: boolean;
  onClose: () => void;
}

const TestEmailDialog = ({ open, onClose }: TestEmailDialogProps) => {
  const [to, setTo] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const testMutation = useMutation({
    mutationFn: (email: string) => settingsApi.testEmail(email),
    onSuccess: () => {
      enqueueSnackbar(`Testovací e-mail odeslán na ${to}`, { variant: 'success' });
      onClose();
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba odeslání', { variant: 'error' }),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: () => setTo('') }}
    >
      <DialogTitle>Otestovat SMTP</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Příjemce e-mailu"
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            fullWidth
            autoFocus
            placeholder="vase@adresa.cz"
          />
          <Alert severity="info">
            Synchronní odeslání s aktuálním uloženým nastavením. Pokud vznikne chyba, dostanete zprávu se zdrojem.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={testMutation.isPending}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => testMutation.mutate(to)}
          disabled={!to || testMutation.isPending}
        >
          {testMutation.isPending ? 'Odesílám…' : 'Odeslat test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
